import { Link, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import useAsyncUpdate from "../util/useAsyncUpdate";
import Api from "../api/Api";
import { useLocation } from "react-router-dom";
import { AvailabilityWithPage, Course, Store } from "../api/ApiData";
import parse from "html-react-parser";
import { StaticTimeTable } from "../components/TimeTable/StaticTimeTable";
import TagManager from "react-gtm-module";
import { useApiContext } from "../api/ApiProvider";

export default function LpPage() {
  const { api } = useApiContext();
  const location = useLocation();
  const [group] = useAsyncUpdate(api.GetGroup);
  const [store, setStore] = useState<Store>();
  const [course, setCourse] = useState<Course>();
  const [availabilityWithPage, setAvailabilityWithPage] = useState<AvailabilityWithPage | null>();
  const [pageContent, setPageContent] = useState<string | null>();
  const [bottomFloatOpacity, setBottomFloatOpacity] = useState(0);
  const queryParams = new URLSearchParams(window.location.search);
  const storeId = queryParams.get("shop");
  const courseId = queryParams.get("course");
  const pageBaseUrl = `/static/lp/group_${group?.id}/shop_${storeId}/course_${courseId}/`;

  useEffect(() => {
    api.GetStores().then((stores) => {
      setStore(stores?.find((store) => store.id.toString() === storeId));
    });
  }, []);

  useEffect(() => {
    if (store) {
      api.GetCourses(store.id).then((courses) => {
        setCourse(courses?.find((course) => course.id.toString() === courseId));
      });

      if (store.google_tag_manager_id !== "") {
        TagManager.initialize({ gtmId: store.google_tag_manager_id });
      }
    }
  }, [store]);

  useEffect(() => {
    if (course && store) {
      api.GetNearestAvailability(store, course).then(setAvailabilityWithPage);
    }
  }, [course]);

  useEffect(() => {
    if (!store || !course || !availabilityWithPage || !group) {
      return;
    }

    fetch(`${pageBaseUrl}index.html`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        document.title = doc.title;

        // ページのベースURLを変更して画像等のリソースが正常に読み込めるようにする
        const baseElement = doc.createElement("base");
        baseElement.href = pageBaseUrl;
        doc.head.prepend(baseElement);

        // apple環境で<img>タグを読み込む場合、<base>タグを使ったベースURL指定がうまく動かないためsrcごと書き換える
        const imgTags = doc.querySelectorAll("img");
        imgTags.forEach((img) => {
          const currentSrc = img.getAttribute("src");
          if (currentSrc && !currentSrc.startsWith("http://") && !currentSrc.startsWith("https://")) {
            const newSrc = pageBaseUrl + currentSrc.replace(/^\.\//, "");
            img.setAttribute("src", newSrc);
          }
        });

        // カレンダー要素を探して置き換えを行う
        const calenderElement = doc.querySelector("reservation-calender");
        if (calenderElement) {
          calenderElement.outerHTML = ReactDOMServer.renderToString(
            <Stack style={{ padding: "8px", alignItems: "center", lineHeight: 1.5, width: "100%" }}>
              <Stack style={{ width: "100%" }}>
                <Stack style={{ backgroundColor: "#a87d52", width: "100%", padding: "16px" }}>
                  <Typography style={{ color: "white", fontFamily: "ＭＳ Ｐ明朝", fontWeight: "bold", margin: "0px" }}>{store.name}</Typography>
                </Stack>
                <StaticTimeTable page={availabilityWithPage.page} store={store} availability={availabilityWithPage.availability} cellLink={`/reserve/${location.search}`} rowCount={6} />
              </Stack>
            </Stack>
          );
        }

        // ボタン要素を探して置き換えを行う
        const reservationButtonElements = doc.querySelectorAll("reservation-button");
        reservationButtonElements.forEach((reservationButtonElement) => {
          // スタイルと子要素を引き継ぐ
          const reservationButtonStyle = getStyleFromElement(reservationButtonElement);
          reservationButtonElement.outerHTML = ReactDOMServer.renderToString(
            <Link href={`/reserve/${location.search}`} style={reservationButtonStyle}>
              {parse(reservationButtonElement.innerHTML)}
            </Link>
          );
        });

        setPageContent(doc.documentElement.outerHTML);
      })
      .catch((error) => {
        console.error("ページ読み込み中にエラーが発生しました: ", error);
        setPageContent(null);
      });
  }, [availabilityWithPage]);

  useEffect(() => {
    document.title = `コース説明 -${group?.domain}-`;

    const handleScroll = () => {
      let opacity = document.documentElement.scrollTop > 100 ? 1 : 0;
      setBottomFloatOpacity(opacity);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (pageContent === null || group === null || store === null || course === null) {
    return <>ページのロードに失敗しました。</>;
  }

  if (pageContent === undefined || group === undefined || store === undefined || course === undefined) {
    return <>ページをロード中です...</>;
  }

  return (
    <Stack>
      <Stack zIndex={1} width="100%" left={0} top={0} justifyContent="center" alignItems="center" position="sticky" padding="4px 8px" bgcolor="white" boxShadow="0 0 5px gray">
        <Stack maxWidth="800px" width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <img src={`${pageBaseUrl}resource/images/float/Header-Logo.png`} style={{ maxWidth: "100%", maxHeight: "50px" }} />
          <Stack direction="row" maxWidth="100%" maxHeight="50px">
            <Link href={`tel:${store.phone}`} display="flex" alignItems="center">
              <img src={`${pageBaseUrl}resource/images/button/Reservation_Button_Phone.png`} style={{ maxWidth: "100%", maxHeight: "50px" }} />
            </Link>
            <Link href={`/reserve/${location.search}`} display="flex" alignItems="center">
              <img src={`${pageBaseUrl}resource/images/button/Reservation_Button_Web.png`} style={{ maxWidth: "100%", maxHeight: "50px" }} />
            </Link>
          </Stack>
        </Stack>
      </Stack>
      <div dangerouslySetInnerHTML={{ __html: pageContent }} />
      <Stack width="100%" bottom={0} justifyContent="center" alignItems="center" position="sticky" padding="4px 8px" bgcolor="#634070" sx={{ opacity: bottomFloatOpacity, transition: "opacity 0.5s ease-in-out" }}>
        <Stack maxWidth="800px" width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="white" fontFamily="ＭＳ Ｐ明朝">
            ご予約、空き枠検索はこちらから
          </Typography>
          <Link href={`/reserve/${location.search}`} height="50px">
            <img src={`${pageBaseUrl}resource/images/button/Reservation_Button_Web.png`} height="50px" />
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
}

function getStyleFromElement(element: Element | null) {
  const styleString = element?.getAttribute("style");

  return styleString
    ? styleString.split(";").reduce((acc: { [key: string]: string }, style) => {
        const [key, value] = style.split(":");
        if (key && value) {
          acc[key.trim()] = value.trim();
        }
        return acc;
      }, {})
    : {};
}
